// ============================================================================
// Blogger API client — pulls the Vastavik blog's posts so they render inside
// vastavik.org itself (no redirect to blogspot). Read-only, public data.
//
// The API key is a browser key: it is visible in the shipped bundle, which is
// expected for client-side Google APIs. Lock it down in Google Cloud Console:
// restrict it to the Blogger API v3 and to the vastavik.org HTTP referrer.
// Both values can be overridden at build time via CRA env vars without a code
// change (REACT_APP_BLOGGER_API_KEY / REACT_APP_BLOGGER_BLOG_ID).
// ============================================================================

const API_KEY =
  process.env.REACT_APP_BLOGGER_API_KEY || 'AIzaSyCQ9g_wrPayi_0xrO8Yl17nQdXfOYmAO00';
const BLOG_ID =
  process.env.REACT_APP_BLOGGER_BLOG_ID || '5282419187397808353';

const BASE = 'https://www.googleapis.com/blogger/v3';

export interface BlogPost {
  id: string;
  title: string;
  content: string;      // HTML authored by the blog owner
  published: string;    // ISO date
  updated?: string;
  url?: string;         // canonical blogspot URL (used only for share/fallback)
  images?: { url: string }[];
  labels?: string[];
  author?: { displayName?: string; image?: { url?: string } };
}

export interface PostList {
  items: BlogPost[];
  nextPageToken?: string;
}

async function get<T>(path: string, params: Record<string, string>): Promise<T> {
  const qs = new URLSearchParams({ key: API_KEY, ...params }).toString();
  const res = await fetch(`${BASE}${path}?${qs}`);
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      msg = body?.error?.message || msg;
    } catch {
      /* keep default */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

/** List published posts, newest first. Pass the previous nextPageToken to page. */
export async function listPosts(pageToken?: string, maxResults = 12): Promise<PostList> {
  const params: Record<string, string> = {
    maxResults: String(maxResults),
    fetchImages: 'true',
    status: 'live',
    orderBy: 'published',
  };
  if (pageToken) params.pageToken = pageToken;
  const data = await get<{ items?: BlogPost[]; nextPageToken?: string }>(
    `/blogs/${BLOG_ID}/posts`,
    params
  );
  return { items: data.items || [], nextPageToken: data.nextPageToken };
}

/** Fetch a single post by id, including its full HTML content. */
export async function getPost(postId: string): Promise<BlogPost> {
  return get<BlogPost>(`/blogs/${BLOG_ID}/posts/${postId}`, { fetchImages: 'true' });
}

/** First image for a post: the API's images[], else the first <img> in content. */
export function postThumbnail(post: BlogPost): string | null {
  if (post.images && post.images[0]?.url) return post.images[0].url;
  const m = /<img[^>]+src=["']([^"']+)["']/i.exec(post.content || '');
  return m ? m[1] : null;
}

/** Plain-text excerpt from a post's HTML content, trimmed to `max` chars. */
export function postExcerpt(post: BlogPost, max = 160): string {
  const text = (post.content || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
}

export function formatBlogDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}
